/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TAB: Multimedia HC
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * API para que el sistema externo de Historia Clínica consulte
 * los documentos médicos firmados de un paciente.
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../utils/apiConfig';

function MultimediaHcTab({ onSuccess, onError }) {
  const [stats, setStats]           = useState(null);
  const [cargando, setCargando]     = useState(true);
  const [mostrarKey, setMostrarKey] = useState(false);
  const [descargando, setDescargando]   = useState(false);

  const correoUsuario = localStorage.getItem('correo') || '';
  const rol           = localStorage.getItem('rol')    || '';

  // ⚠️ NOTA: La API Key real vive en el .env del backend.
  // Aquí solo mostramos los últimos 8 caracteres como referencia visual.
  // El superadmin la conoce y puede regenerarla desde el servidor.
  const API_KEY_PREVIEW = 'hc-multi-•••••••••••••••••••••••••••••••';

  // ── Cargar estadísticas ──
  const cargarStats = useCallback(async () => {
    setCargando(true);
    try {
      const res = await apiClient.get('/api/external/multimedia/stats', {
        headers: {
          'usuario-correo': correoUsuario,
          'usuario-rol':    rol,
          'rol':            rol
        }
      });
      setStats(res.data.stats);
    } catch (err) {
      onError?.('Error al cargar estadísticas');
    } finally {
      setCargando(false);
    }
  }, [correoUsuario, rol, onError]);

  useEffect(() => {
    cargarStats();
  }, [cargarStats]);

  // ── Copiar URL base ──
  const copiarUrl = (texto) => {
    navigator.clipboard.writeText(texto);
    onSuccess?.('Copiado al portapapeles');
  };

  // ── Descargar manual ──
  const descargarManual = async () => {
    setDescargando(true);
    try {
      const response = await apiClient.get('/api/external/multimedia/manual', {
        responseType: 'blob',
        headers: {
          'usuario-correo': correoUsuario,
          'usuario-rol':    rol,
          'rol':            rol
        }
      });

      // Crear URL del blob y forzar descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'BIOFIRMAS_CTX_API_Multimedia_HC.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      onSuccess?.('Manual descargado correctamente');
    } catch (err) {
      onError?.('Error al descargar el manual');
    } finally {
      setDescargando(false);
    }
  };

  // ── URL base del backend ──
  const baseURL = process.env.REACT_APP_NODE_ENV === 'production'
    ? (process.env.REACT_APP_API_URL_PRODUCTION || 'https://biofirmabk.colombianadetrasplantes.com')
    : 'http://localhost:3001';

  return (
    <>
      <style>{`
        .stat-card {
          transition: transform 0.2s;
          cursor: default;
        }
        .stat-card:hover { transform: translateY(-3px); }
        .code-box {
          background: #1e293b;
          color: #e2e8f0;
          padding: 10px 14px;
          border-radius: 8px;
          font-family: 'Consolas', 'Monaco', monospace;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
      `}</style>

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h5 className="fw-bold mb-1" style={{ color: '#1a1e4b' }}>
            <i className="bi bi-collection-play me-2"></i>Multimedia HC
          </h5>
          <p className="text-muted mb-0 small">
            API para que Historia Clínica consulte los documentos médicos firmados de los pacientes
          </p>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={cargarStats}>
          <i className="bi bi-arrow-clockwise me-1"></i>Actualizar
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* ESTADÍSTICAS DE USO                                       */}
      {/* ═══════════════════════════════════════════════════════ */}
      <div className="mb-4">
        <h6 className="fw-semibold mb-3 text-muted">
          <i className="bi bi-bar-chart-line me-2"></i>ESTADÍSTICAS
        </h6>
        <div className="row g-3">
          <div className="col-md-3">
            <div className="card border-0 shadow-sm stat-card" style={{ borderRadius: 12 }}>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="bg-primary bg-opacity-10 rounded p-3 me-3">
                    <i className="bi bi-file-earmark-medical-fill text-primary fs-4"></i>
                  </div>
                  <div>
                    <h3 className="mb-0">{cargando ? '...' : stats?.totalDisponibles ?? 0}</h3>
                    <small className="text-muted">Documentos disponibles</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-0 shadow-sm stat-card" style={{ borderRadius: 12 }}>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="bg-success bg-opacity-10 rounded p-3 me-3">
                    <i className="bi bi-people-fill text-success fs-4"></i>
                  </div>
                  <div>
                    <h3 className="mb-0">{cargando ? '...' : stats?.pacientesUnicos ?? 0}</h3>
                    <small className="text-muted">Pacientes únicos</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-0 shadow-sm stat-card" style={{ borderRadius: 12 }}>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="bg-info bg-opacity-10 rounded p-3 me-3">
                    <i className="bi bi-calendar-week text-info fs-4"></i>
                  </div>
                  <div>
                    <h3 className="mb-0">{cargando ? '...' : stats?.ultimaSemana ?? 0}</h3>
                    <small className="text-muted">Última semana</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-0 shadow-sm stat-card" style={{ borderRadius: 12 }}>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="bg-warning bg-opacity-10 rounded p-3 me-3">
                    <i className="bi bi-calendar-day text-warning fs-4"></i>
                  </div>
                  <div>
                    <h3 className="mb-0">{cargando ? '...' : stats?.documentosHoy ?? 0}</h3>
                    <small className="text-muted">Hoy</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* CONFIGURACIÓN DE LA API                                  */}
      {/* ═══════════════════════════════════════════════════════ */}
      <div className="mb-4">
        <h6 className="fw-semibold mb-3 text-muted">
          <i className="bi bi-key me-2"></i>CONFIGURACIÓN
        </h6>
        <div className="card border-0 shadow-sm" style={{ borderRadius: 12 }}>
          <div className="card-body p-4">

            {/* Estado */}
            <div className="row mb-3">
              <div className="col-md-3">
                <strong className="text-muted small">ESTADO</strong>
              </div>
              <div className="col-md-9">
                <span className="badge bg-success" style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
                  <i className="bi bi-check-circle-fill me-1"></i>Activa
                </span>
              </div>
            </div>

            {/* URL Base */}
            <div className="row mb-3">
              <div className="col-md-3">
                <strong className="text-muted small">URL BASE</strong>
              </div>
              <div className="col-md-9">
                <div className="code-box">
                  <span>{baseURL}</span>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => copiarUrl(baseURL)}
                    title="Copiar"
                  >
                    <i className="bi bi-clipboard"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Endpoint principal */}
            <div className="row mb-3">
              <div className="col-md-3">
                <strong className="text-muted small">ENDPOINT LISTA</strong>
              </div>
              <div className="col-md-9">
                <div className="code-box">
                  <span>GET /api/external/multimedia/documentos/:cedula</span>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => copiarUrl('/api/external/multimedia/documentos/')}
                    title="Copiar"
                  >
                    <i className="bi bi-clipboard"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Endpoint ver */}
            <div className="row mb-3">
              <div className="col-md-3">
                <strong className="text-muted small">ENDPOINT VER PDF</strong>
              </div>
              <div className="col-md-9">
                <div className="code-box">
                  <span>GET /api/external/multimedia/ver/:documentoId</span>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => copiarUrl('/api/external/multimedia/ver/')}
                    title="Copiar"
                  >
                    <i className="bi bi-clipboard"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* API Key */}
            <div className="row">
              <div className="col-md-3">
                <strong className="text-muted small">API KEY</strong>
              </div>
              <div className="col-md-9">
                <div className="code-box">
                  <span>{API_KEY_PREVIEW}</span>
                  <div className="d-flex gap-1">
                    <button
                      className="btn btn-sm btn-light"
                      onClick={() => setMostrarKey(!mostrarKey)}
                      title="La API Key real está en el .env del servidor"
                    >
                      <i className="bi bi-info-circle"></i>
                    </button>
                  </div>
                </div>
                {mostrarKey && (
                  <div className="alert alert-info mt-2 mb-0 small">
                    <i className="bi bi-info-circle me-2"></i>
                    Por seguridad, la API Key real está almacenada en el archivo <code>.env</code> del
                    servidor (variable <code>API_KEY_HC_MULTIMEDIA</code>). Solo el superadmin
                    con acceso al servidor puede consultarla o regenerarla.
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* MANUAL DE INTEGRACIÓN PARA HC                            */}
      {/* ═══════════════════════════════════════════════════════ */}
      <div className="mb-4">
        <h6 className="fw-semibold mb-3 text-muted">
          <i className="bi bi-book me-2"></i>DOCUMENTACIÓN
        </h6>
        <div className="card border-0 shadow-sm" style={{ borderRadius: 12 }}>
          <div className="card-body p-4">

            <div className="row align-items-center">
              <div className="col-md-1 text-center">
                <i className="bi bi-file-earmark-word-fill text-primary" style={{ fontSize: '3rem' }}></i>
              </div>
              <div className="col-md-7">
                <h6 className="fw-bold mb-1">Manual de Integración para el equipo HC</h6>
                <p className="text-muted small mb-0">
                  Documento técnico (.docx) con endpoints, ejemplos de código, autenticación
                  y consideraciones de seguridad. Listo para entregar al equipo técnico de
                  Historia Clínica.
                </p>
              </div>
              <div className="col-md-4 text-end">
                <button
                  className="btn btn-primary"
                  onClick={descargarManual}
                  disabled={descargando}
                >
                  {descargando ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Descargando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-download me-2"></i>
                      Descargar Manual
                    </>
                  )}
                </button>
              </div>
            </div>

            <hr className="my-3" />

            <div className="d-flex align-items-center text-muted small">
              <i className="bi bi-info-circle me-2"></i>
              <span>
                Si actualizas el manual, reemplaza el archivo en{' '}
                <code>backend/assets/manuales/</code> del servidor.
              </span>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default MultimediaHcTab;